import type { MessageService } from '../service/message.service';
import {
  Settlement,
  SettlementBulkResponse,
  SettlementBulkResult,
  SkippedSettlement,
  SkippedSettlementReason,
} from '../model/settlement';

export const PAYMENT_FINANCIAL_COMMAND_IN_PROGRESS = 'PAYMENT_FINANCIAL_COMMAND_IN_PROGRESS';

const SKIPPED_SETTLEMENTS_TITLE = '일부 정산을 바꾸지 않았어요';

const SKIPPED_SENTENCES: Record<SkippedSettlementReason, (count: number, paymentIds: string) => string> = {
  PAYMENT_LOCKED: (count, paymentIds) =>
    `${count}건은 결제 잠금 때문에 그대로 뒀어요${paymentIds}. 잠금이 풀린 뒤 다시 처리해 주세요.`,
  COMPLETED: (count, paymentIds) => `${count}건은 이미 완료된 정산이라 그대로 뒀어요${paymentIds}.`,
};

const SHOWN_PAYMENT_ID_LIMIT = 10;

/** 인터셉터가 이 409 를 안내하므로 화면의 오류 처리는 따로 팝업을 띄우지 않는다. */
export function isPaymentGateHeldError(error: unknown): boolean {
  const response = error as { status?: unknown; error?: { message?: unknown } } | null;
  return response?.status === 409 && response.error?.message === PAYMENT_FINANCIAL_COMMAND_IN_PROGRESS;
}

export function paymentGateHeldDescription(paymentId?: number | null): string {
  const payment = paymentId == null ? '' : `(예약번호 ${paymentId})`;
  return (
    `다른 환불·결제 변경이 진행 중이거나 PG 결과 확인이 필요한 결제예요${payment}. ` +
    '개발팀에 결제 잠금 해제를 요청해 주세요.'
  );
}

/** 건너뛴 정산을 알려 주기 전의 Nest 는 바꾼 정산 배열만 주고, 완료 정산을 알리기 전의 Nest 는 reason 없이 잠긴 정산만 준다. */
export function toSettlementBulkResult(resp: Settlement[] | SettlementBulkResponse | null): SettlementBulkResult {
  if (Array.isArray(resp)) return { settlements: resp, skipped: [] };
  return {
    settlements: resp?.settlements ?? [],
    skipped: (resp?.skipped ?? []).map((row) => ({ ...row, reason: row.reason ?? 'PAYMENT_LOCKED' })),
  };
}

export function skippedSettlementsMessage(selectedCount: number, result: SettlementBulkResult): string | null {
  const sentences = (Object.keys(SKIPPED_SENTENCES) as SkippedSettlementReason[]).flatMap((reason) => {
    const rows = result.skipped.filter((row) => row.reason === reason);
    if (rows.length === 0) return [];
    const count = new Set(rows.map((row) => row.settlementId)).size;
    return [SKIPPED_SENTENCES[reason](count, paymentIdList(rows))];
  });
  return sentences.length === 0 ? null : `선택한 ${selectedCount}건 중 ${sentences.join('\n')}`;
}

/** 팝업은 마지막 한 건만 보이므로, 건너뛴 정산을 알렸으면 true 를 돌려 호출자가 완료 안내를 띄우지 않게 한다. */
export function popupSkippedSettlements(
  messageService: Pick<MessageService, 'popup'>,
  selectedCount: number,
  result: SettlementBulkResult,
): boolean {
  const description = skippedSettlementsMessage(selectedCount, result);
  if (!description) return false;
  messageService.popup({ title: SKIPPED_SETTLEMENTS_TITLE, description, cancelLabel: '확인' });
  return true;
}

function paymentIdList(rows: SkippedSettlement[]): string {
  const paymentIds = [...new Set(rows.map((row) => row.paymentId).filter((id): id is number => id != null))].sort(
    (a, b) => a - b,
  );
  if (paymentIds.length === 0) return '';
  const shown = paymentIds.slice(0, SHOWN_PAYMENT_ID_LIMIT).join(', ');
  const rest = paymentIds.length > SHOWN_PAYMENT_ID_LIMIT ? ` 외 ${paymentIds.length - SHOWN_PAYMENT_ID_LIMIT}건` : '';
  return `: 예약번호 ${shown}${rest}`;
}
